import 'react-datepicker/dist/react-datepicker.css';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';

import clientIcon from '../../../../assets/icons/clients-small.svg';
import saveBigIcon from '../../../../assets/icons/save.svg';
import {
  CLIENT_SOURCE_OPTIONS,
  statusClientOptions,
} from '../../../../config/constants';
import { ROLES } from '../../../../config/permissions';
import { useModal } from '../../../../hooks/useModal';
import { clientFiltersState } from '../../../../recoil/clientsFilter';
import { needRefreshState } from '../../../../recoil/needRefresh';
import { convertDateToString } from '../../../../utils/convertDate';
import Breadcrumb from '../../../ui/common/Breadcrumb';
import { Button } from '../../../ui/common/Button';
import DropdownMultiple from '../../../ui/common/DropdownMultiple';
import DateField from '../../../ui/common/Inputs/DateField';
import DropdownUsers from '../../../ui/common/Inputs/DropdownUsers';

const FilterClient = () => {
  const { t } = useTranslation('common');

  // Form title
  const breadcrumbTitle = `${t('clients.title-h1')}`;
  const breadcrumbSecondTitle = t('modalFilterClient.filters');

  // Change data for refresh tables
  const setNeedRefresh = useSetRecoilState(needRefreshState);

  const refreshOrder = () => {
    setNeedRefresh(true);
  };

  const endRefreshOrder = () => {
    setTimeout(() => {
      setNeedRefresh(false);
    }, 500);
  };

  // On submit success close modal notification
  const { handleCloseAllModals } = useModal();

  const closeOrder = () => {
    handleCloseAllModals();
    refreshOrder();
    endRefreshOrder();
  };

  // Filters
  const [filters, setFilters] = useRecoilState(clientFiltersState);

  const [signedByFilter, setSignedByFilter] = useState<string[]>([]);
  const [signedByNames, setSignedByNames] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [signedAtFromFilter, setSignedAtFromFilter] = useState<string>('');
  const [signedAtToFilter, setSignedAtToFilter] = useState<string>('');

  const [lastServiceStartDateFilter, setLastServiceStartDateFilter] =
    useState<string>('');
  const [lastServiceEndDateFilter, setLastServiceEndDateFilter] =
    useState<string>('');

  useEffect(() => {
    if (filters?.statusFilter) {
      const updatedStatusFilter = [...filters.statusFilter];
      setStatusFilter(updatedStatusFilter);
    }

    if (filters?.lastServiceStartDateFilter) {
      setLastServiceStartDateFilter(filters.lastServiceStartDateFilter);
    }

    if (filters?.lastServiceEndDateFilter) {
      setLastServiceEndDateFilter(filters.lastServiceEndDateFilter);
    }

    if (filters?.signedBy) {
      setSignedByFilter(filters.signedBy);
    }

    if (filters?.signedAtFrom) {
      setSignedAtFromFilter(filters.signedAtFrom);
    }

    if (filters?.signedAtTo) {
      setSignedAtToFilter(filters.signedAtTo);
    }

    if (filters?.source) {
      const updatedSourceFilter = [...filters.source];
      setSourceFilter(updatedSourceFilter);
    }

    if (filters?.signedByNames) {
      setSignedByNames(filters.signedByNames);
    }
  }, [filters]);

  const handleStatusFilterChange = (status: string[]) => {
    // const updatedFilter = statusFilter.includes(status)
    //   ? statusFilter.filter((item) => item !== status)
    //   : [...statusFilter, status];

    setStatusFilter(status);
  };

  const handleFiltersSave = () => {
    let newFilters: Record<string, any> = {};

    newFilters = { ...filters };

    if (statusFilter.length > 0) {
      newFilters.statusFilter = statusFilter;
    } else {
      newFilters.statusFilter = null;
    }

    if (lastServiceStartDateFilter) {
      newFilters.lastServiceStartDateFilter = lastServiceStartDateFilter;
    } else {
      newFilters.lastServiceStartDateFilter = null;
    }

    if (lastServiceEndDateFilter) {
      newFilters.lastServiceEndDateFilter = lastServiceEndDateFilter;
    } else {
      newFilters.lastServiceEndDateFilter = null;
    }

    if (signedByFilter) {
      newFilters.signedBy = signedByFilter;
    } else {
      newFilters.signedBy = null;
    }

    if (signedByNames) {
      newFilters.signedByNames = signedByNames;
    } else {
      newFilters.signedByNames = null;
    }

    if (signedAtFromFilter) {
      newFilters.signedAtFrom = signedAtFromFilter;
    } else {
      newFilters.signedAtFrom = null;
    }

    if (signedAtToFilter) {
      newFilters.signedAtTo = signedAtToFilter;
    } else {
      newFilters.signedAtTo = null;
    }

    if (sourceFilter.length > 0) {
      newFilters.source = sourceFilter;
    } else {
      newFilters.source = null;
    }

    if (Object.keys(newFilters).length > 0) {
      setFilters(newFilters);
    } else {
      setFilters(null);
    }
    closeOrder();
  };

  // const [statusDropdownOpen, setStatusDropdownOpen] = useState<boolean>(false);

  return (
    <>
      <Breadcrumb
        title={breadcrumbTitle}
        secondTitle={breadcrumbSecondTitle}
        image={clientIcon}
      />
      <h1 className="mt-6 text-xl font-[600]">
        {t('modalFilterClient.filters')}
      </h1>
      <div className="my-6 flex flex-col gap-2">
        {/* Status Dropdown */}
        <div className="h-[60px]">
          <DropdownMultiple
            options={statusClientOptions}
            onChange={handleStatusFilterChange}
            label={t('modalFilterClient.status')}
            selectedValues={statusFilter || []}
          />
        </div>
        {/* Start Date Filter Datepicker */}
        <DateField
          selectedValue={
            lastServiceStartDateFilter
              ? new Date(lastServiceStartDateFilter).getTime().toString()
              : ''
          }
          onSelectChange={(date: any) => {
            setLastServiceStartDateFilter(convertDateToString(date));
          }}
          label={t('modalFilterClient.startDateLastService')}
          placeholder={t('global.select-date')}
        />

        {/* End Date Filter Datepicker */}
        <DateField
          selectedValue={
            lastServiceEndDateFilter
              ? new Date(lastServiceEndDateFilter).getTime().toString()
              : ''
          }
          onSelectChange={(date: any) => {
            setLastServiceEndDateFilter(convertDateToString(date));
          }}
          label={t('modalFilterClient.endDateLastService')}
          placeholder={t('global.select-date')}
        />

        {/* Source Dropdown */}
        <div className="h-[60px]">
          <DropdownMultiple
            options={CLIENT_SOURCE_OPTIONS}
            onChange={(val) => setSourceFilter(val)}
            label={t('modalFilterClient.source')}
            selectedValues={sourceFilter || []}
          />
        </div>

        <DropdownUsers
          label={t('modalFilterClient.signedBy')}
          selectedValue={signedByFilter || []}
          onSelectChange={(value) => setSignedByFilter(value as string[])}
          onSelectChangeLabel={(val) => setSignedByNames(val as string[])}
          fullWidth
          selectClassname="!font-normal border-b-2 border-t-0 border-graydark"
          roles={[
            ROLES.OWNER,
            ROLES.MANAGER,
            ROLES.SUPERVISOR,
            ROLES.ACCOUNTING,
            ROLES.SALESMAN,
          ]}
          multi
        />

        {/* Start Date Signed At Filter Datepicker */}
        <DateField
          selectedValue={
            signedAtFromFilter
              ? new Date(signedAtFromFilter).getTime().toString()
              : ''
          }
          onSelectChange={(date: any) => {
            setSignedAtFromFilter(convertDateToString(date));
          }}
          label={t('modalFilterClient.signedAtFrom')}
          placeholder={t('global.select-date')}
        />

        {/* End Date Signed At Filter Datepicker */}
        <DateField
          selectedValue={
            signedAtToFilter
              ? new Date(signedAtToFilter).getTime().toString()
              : ''
          }
          onSelectChange={(date: any) => {
            setSignedAtToFilter(convertDateToString(date));
          }}
          label={t('modalFilterClient.signedAtTo')}
          placeholder={t('global.select-date')}
        />

        <div className="flex justify-end">
          <Button
            text={t('modalNewClient.save')}
            variant="green"
            icon={saveBigIcon}
            onClick={handleFiltersSave}
            type="button"
            id="save-client-filters"
          />
        </div>
      </div>
    </>
  );
};

export default FilterClient;
